import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';
import { Report } from '@/types/report';

export const useUserReports = (teacherId?: string) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<string>('teaching_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const itemsPerPage = 10; // Changed from 5 back to 10
  
  const fetchReports = useCallback(async () => {
    if (!teacherId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Count total records first (with search filter if present)
      let countQuery = supabase
        .from('student_development_reports')
        .select('id', { count: 'exact' })
        .eq('teacher_id', teacherId);
        
      // Apply search if provided
      if (searchQuery) {
        countQuery = countQuery.or(`student_nickname.ilike.%${searchQuery}%,student_full_name.ilike.%${searchQuery}%,subject.ilike.%${searchQuery}%`);
      }
      
      const { count, error: countError } = await countQuery;
      
      if (countError) throw countError;
      
      // Calculate total pages
      const total = count || 0;
      const pages = Math.ceil(total / itemsPerPage);
      setTotalPages(pages || 1);
      
      // Adjust current page if it's out of bounds
      if (currentPage > pages && pages > 0) {
        setCurrentPage(1);
      }
      
      // Fetch paginated data
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      
      let query = supabase
        .from('student_development_reports')
        .select('*')
        .eq('teacher_id', teacherId)
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range(from, to);
        
      // Apply search if provided
      if (searchQuery) {
        query = query.or(`student_nickname.ilike.%${searchQuery}%,student_full_name.ilike.%${searchQuery}%,subject.ilike.%${searchQuery}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [teacherId, currentPage, searchQuery, sortField, sortDirection]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page on sort change
  };
  
  // Format date for display
  const formatDate = (date: string | null) => {
    if (!date) return '';
    return format(new Date(date), 'dd MMM yyyy', { locale: th });
  };
  
  // Format time for display in HH:mm format
  const formatTime = (timeString: string | null) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      return format(date, 'HH:mm');
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  return {
    reports,
    loading,
    currentPage,
    totalPages,
    searchQuery,
    sortField,
    sortDirection,
    handleSearch,
    handleSort,
    handlePageChange,
    formatDate,
    formatTime
  };
};
